import React from 'react';

import { Tooltip, Badge } from 'antd';

import styled from 'styled-components';

import { showDateNoTime } from '@libs/NwMoment';

import { useUserPermission } from '@contexts/UserContext';

import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';
import { getClientStatementStatus } from '@components/StatementRowsCommon/helpers';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.span`
    font-size: ${(props) => (props.$small ? '1.1rem' : '1.25rem')};
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 2.5rem;
    color: ${(props) => (props.$green ? props.theme.SuccessColor : props.theme.TextColor)};

    .ant-badge {
        padding: 0 0.15rem;
        .ant-badge-status-dot {
            width: ${(props) => (props.$small ? '.6rem' : '.75rem')};
            height: ${(props) => (props.$small ? '.6rem' : '.75rem')};
        }
    }
`;

const StatementClientInvoiceStatusIcon = ({
    transaction,
    statusModel,
    small,
    additionalText,
    onInvoiceClick,
    canOpenInvoice,
}) => {
    const accessAccounting = useUserPermission('AccessAccounting');
    const invoiceInteractionEnabled = typeof canOpenInvoice === 'boolean' ? canOpenInvoice : accessAccounting;

    const resolvedStatus = statusModel || getClientStatementStatus(transaction);
    const { state, clientId, invoiceNumber, invoiceDate, invoiceId, invoicePosted } = resolvedStatus;
    const isInvoiceClickable = invoiceInteractionEnabled && Boolean(invoiceId);

    const addtxt = () => {
        if (additionalText) {
            return additionalText + ' - ';
        }
        return '';
    };

    const ColorMap = {
        option: StyleVariables.WarningColor,
        job: StyleVariables.DangerColor,
        billable: StyleVariables.SuccessColor,
    };
    const TextMap = {
        option: 'Option',
        job: 'Confirmed',
        billable: 'To Bill',
    };

    const handleInvoiceClick = (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (isInvoiceClickable) {
            if (typeof onInvoiceClick === 'function' && invoiceId) {
                onInvoiceClick(invoiceId);
            }
        }
    };

    const Content = () => {
        if (!state) {
            return null;
        }

        const clientstatus = state;

        if (!clientId) {
            return (
                <Wrapper $small={small}>
                    <Tooltip title="No clients involved">
                        <NwIcon opacity={0.25} icon={byPrefixAndName.fasl['slash-forward']} />
                    </Tooltip>
                </Wrapper>
            );
        }
        if (invoicePosted) {
            if (clientstatus === 'paid') {
                //here I need to show the file-export icon with a green overlaid check icon
                return (
                    <Wrapper $small={small}>
                        <Tooltip
                            title={`Invoice ${invoiceNumber ? `#${invoiceNumber}` : 'draft'} posted to external software on ${showDateNoTime(invoiceDate)} - PAID`}
                        >
                            <NwIcon
                                icon={byPrefixAndName.fasds['file-export']}
                                style={{
                                    '--fa-primary-color': `${StyleVariables.SuccessColor}`,
                                    '--fa-secondary-color': `${StyleVariables.SuccessColor}`,
                                    '--fa-primary-opacity': '1',
                                    '--fa-secondary-opacity': '.4',
                                }}
                                pointer={isInvoiceClickable}
                                onClick={isInvoiceClickable ? handleInvoiceClick : undefined}
                            />
                        </Tooltip>
                    </Wrapper>
                );
            } else {
                return (
                    <Wrapper $small={small}>
                        <Tooltip
                            title={`Invoice ${invoiceNumber ? `#${invoiceNumber}` : 'draft'} posted to external software on ${showDateNoTime(invoiceDate)}`}
                        >
                            <NwIcon
                                icon={byPrefixAndName.fasl['file-export']}
                                opacity={invoiceNumber ? 1 : 0.5}
                                pointer={isInvoiceClickable}
                                onClick={isInvoiceClickable ? handleInvoiceClick : undefined}
                            />
                        </Tooltip>
                    </Wrapper>
                );
            }
        }
        switch (clientstatus) {
            case 'locked':
                return (
                    <Wrapper $small={small}>
                        <Tooltip title={'In a invoice draft'}>
                            <NwIcon
                                icon={byPrefixAndName.fasl['lock']}
                                pointer={isInvoiceClickable}
                                onClick={isInvoiceClickable ? handleInvoiceClick : undefined}
                            />
                        </Tooltip>
                    </Wrapper>
                );
            case 'billed':
            case 'invoiced':
                return (
                    <Wrapper $small={small}>
                        <Tooltip title={`${addtxt()}Invoice #${invoiceNumber} - ${showDateNoTime(invoiceDate)}`}>
                            <NwIcon
                                icon={byPrefixAndName.fasl['file-invoice']}
                                pointer={isInvoiceClickable}
                                onClick={isInvoiceClickable ? handleInvoiceClick : undefined}
                            />
                        </Tooltip>
                    </Wrapper>
                );
            case 'paid':
                return (
                    <Wrapper $small={small} $green>
                        <Tooltip title={`${addtxt()}Paid - Invoice #${invoiceNumber} - ${showDateNoTime(invoiceDate)}`}>
                            <NwIcon
                                icon={byPrefixAndName.fass['check']}
                                pointer={isInvoiceClickable}
                                onClick={isInvoiceClickable ? handleInvoiceClick : undefined}
                            />
                        </Tooltip>
                    </Wrapper>
                );
            case 'billable':
            case 'job':
            case 'option':
                return (
                    <Wrapper $small={small}>
                        <Tooltip title={`Waiting for invoicing - ${TextMap[clientstatus]} `}>
                            <NwIcon opacity={0.25} icon={byPrefixAndName.fasl['hourglass']} />
                            <Badge color={ColorMap[clientstatus]} />
                        </Tooltip>
                    </Wrapper>
                );
            default:
                return (
                    <Wrapper $small={small}>
                        <Tooltip title="Waiting for invoicing">
                            <NwIcon opacity={0.25} icon={byPrefixAndName.fasl['hourglass']} />
                        </Tooltip>
                    </Wrapper>
                );
        }
    };
    return <Content />;
};

export default StatementClientInvoiceStatusIcon;
